import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { IconText, Icon, Tooltip } from '@components/UI'
import { ResizeObserverPony, ClassroomManager } from '@helpers'
import { useClassroomPermissions } from '../../contexts'
import { Classroom as CC } from '@constants'

import { Close, Maximize, Minimize, Undo, Redo } from './Tools'

const Toolbar = ({ component, tools, close, history, hideComponentName }) => {
  const { checkPermission } = useClassroomPermissions()
  const canClosePanel = checkPermission(CC.FEATURES.TOOL_PANEL_TOGGLE)

  let [left = [], right = []] = tools
  const [width, setWidth] = useState(0)
  const [diff, setDiff] = useState(0)
  const [fullscreen, setFullscreen] = useState(
    ClassroomManager.getFullscreen() === component.type
  )
  const visibleToolsCount = Math.floor((width - diff) / 33)

  const mainWrapper = useRef(null)
  const leftWrapper = useRef(null)

  useEffect(() => {
    setDiff(mainWrapper.current.clientWidth - leftWrapper.current.clientWidth)

    const observer = new ResizeObserverPony(([entry]) => {
      const { width } = entry.contentRect
      setWidth(width)
    })

    const { current } = mainWrapper
    observer.observe(current)

    return () => {
      observer.unobserve(current)
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(ClassroomManager.getFullscreen() === component.type)
    }
    ClassroomManager.observe('fullscreen', handleFullscreenChange)

    return () => {
      ClassroomManager.unobserve('fullscreen', handleFullscreenChange)
    }
  }, [component.type])

  const isSingleSketchTab =
    !fullscreen &&
    ClassroomManager.getPanels().length == 1 &&
    component.type === 'sketch'

  return (
    <header
      ref={mainWrapper}
      className={`gp-frame-header ${hideComponentName ? 'nameless' : ''}`}
    >
      {!hideComponentName && (
        <div className='component-name'>
          <IconText group='a2' icon={component.icon} value={component.title} />
        </div>
      )}

      <div
        className='component-tools'
        id={`${component.type}-toolbar`}
        style={!left.length ? { borderLeftWidth: 0 } : {}}
      >
        <div style={{ flex: 1 }} ref={leftWrapper}>
          {left
            .slice(0, visibleToolsCount >= 0 ? visibleToolsCount : 0)
            .map((v) => v)}
          {visibleToolsCount < left.length && (
            <div className='control-container dark'>
              <button>
                <Icon name='three dots horizontal' />
              </button>
              <Tooltip
                style={{ padding: '7px 6px', height: '46px', zIndex: 99 }}
                group='c7'
                className='theme color-f background-a border'
                content={
                  <div
                    className='tooltip-content'
                    style={{ display: 'flex', height: '32px' }}
                  >
                    {left
                      .slice(visibleToolsCount >= 0 ? visibleToolsCount : 0)
                      .map((v, i) => {
                        if (i === 0 && v?.key === 'divider') return
                        return v
                      })}
                  </div>
                }
              />
            </div>
          )}
        </div>
        <div>
          {right}
          {!!history && (
            <React.Fragment>
              <Undo onClick={history.undo} />
              <Redo onClick={history.redo} />
            </React.Fragment>
          )}
        </div>
      </div>
      <div className='component-exit'>
        <FullscreenControls
          isFullscreen={fullscreen}
          isSingleSketchTab={isSingleSketchTab}
          component={component}
        />

        {!fullscreen && canClosePanel ? (
          <Close onClick={() => close(component.type)} />
        ) : null}
      </div>
    </header>
  )
}

const FullscreenControls = ({ isFullscreen, isSingleSketchTab, component }) => {
  const { checkPermission } = useClassroomPermissions()
  const canToggleFullscreen = checkPermission(CC.FEATURES.FULL_SCREEN_TOGGLE)

  if (!canToggleFullscreen) return null

  return (
    <div
      className={cx('control-container', 'dark', {
        'control-container--max': isSingleSketchTab
      })}
    >
      {isFullscreen ? (
        <Minimize onClick={() => ClassroomManager.minimize(component.type)} />
      ) : (
        <>
          <Maximize onClick={() => ClassroomManager.maximize(component.type)} />
          {isSingleSketchTab && (
            <>
              Full Screen
              <div className='divider'></div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default Toolbar
